import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import AdBanner from '../components/AdBanner';
import { AD_SLOTS } from '../config/adSlots';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useStructuredData } from '../hooks/useStructuredData';
import { tournamentApi, Tournament } from '../services/api-client';
import { MapPinIcon, CalendarIcon, ScaleIcon } from '../components/icons';
import { formatDateRange } from '../utils/dateTime';

function effectiveStatus(tournament: Tournament): string {
  const now = new Date();
  const start = tournament.startDate ? new Date(tournament.startDate) : null;
  const end = tournament.endDate
    ? new Date(tournament.endDate)
    : tournament.startDate
      ? new Date(tournament.startDate)
      : null;

  // Trust explicit completed/live/in_progress status from the DB
  if (
    tournament.status === 'completed' ||
    tournament.status === 'live' ||
    tournament.status === 'in_progress'
  ) {
    return tournament.status;
  }

  // Derive from dates if status is still "upcoming" but dates say otherwise
  if (end && now > new Date(end.getTime() + 24 * 60 * 60 * 1000)) {
    // More than 1 day after end date — treat as completed
    return 'completed';
  }
  if (start && now >= start && end && now <= new Date(end.getTime() + 24 * 60 * 60 * 1000)) {
    return 'live';
  }
  return 'upcoming';
}

function statusLabel(status: string): { text: string; className: string } {
  switch (status) {
    case 'upcoming':
      return { text: 'Upcoming', className: 'bg-blue-100 text-blue-800' };
    case 'in_progress':
    case 'live':
      return { text: 'Live', className: 'bg-green-100 text-green-800' };
    case 'completed':
      return { text: 'Completed', className: 'bg-gray-100 text-gray-700' };
    default:
      return { text: 'Upcoming', className: 'bg-blue-100 text-blue-800' };
  }
}

function TournamentCard({ tournament }: { tournament: Tournament }) {
  const status = statusLabel(effectiveStatus(tournament));

  return (
    <Link
      to={`/tournaments/${tournament.id}`}
      className="bg-white rounded-lg shadow p-6 flex flex-col hover:shadow-lg transition-shadow"
    >
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${status.className}`}>
          {status.text}
        </span>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-3 hover:text-purple-700 transition-colors">
        {tournament.name}
      </h3>
      <div className="space-y-2 text-sm text-gray-600 mb-4 flex-1">
        {tournament.location && (
          <div className="flex items-center gap-2">
            <MapPinIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{tournament.location}</span>
          </div>
        )}
        {tournament.startDate && (
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{formatDateRange(tournament.startDate, tournament.endDate)}</span>
          </div>
        )}
        {tournament.weightClasses.length > 0 && (
          <div className="flex items-center gap-2">
            <ScaleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span>{tournament.weightClasses.length} weight classes</span>
          </div>
        )}
      </div>
      <span className="text-purple-600 hover:text-purple-800 text-sm font-medium">
        View seeds & results →
      </span>
    </Link>
  );
}

export default function PastTournamentsPage() {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useDocumentMeta(
    'NCAA Wrestling Tournaments — Seeds, Results & Fantasy Scoring',
    'Browse NCAA wrestling tournament results with original seeds, final placements, match-by-match scoring breakdowns, and fantasy points. Free on Takedown League.'
  );

  useStructuredData({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'NCAA Wrestling Tournaments',
    description:
      'Browse NCAA wrestling tournament seeds, results, and fantasy scoring breakdowns on Takedown League.',
    url: 'https://takedownleague.com/tournaments',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Takedown League',
      url: 'https://takedownleague.com',
    },
  });

  useEffect(() => {
    tournamentApi
      .list()
      .then((all) => setTournaments(all))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load tournaments'))
      .finally(() => setLoading(false));
  }, []);

  const live = tournaments.filter((t) => {
    const s = effectiveStatus(t);
    return s === 'live' || s === 'in_progress';
  });
  const upcoming = tournaments.filter((t) => effectiveStatus(t) === 'upcoming');
  const completed = tournaments.filter((t) => effectiveStatus(t) === 'completed');

  // Sort upcoming by start date ascending, completed by start date descending
  upcoming.sort(
    (a, b) => new Date(a.startDate || 0).getTime() - new Date(b.startDate || 0).getTime()
  );
  completed.sort(
    (a, b) => new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime()
  );

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-600 via-purple-600 to-purple-700 text-white py-14 px-4">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-4xl font-bold mb-4">NCAA Wrestling Tournaments</h1>
          <p className="text-purple-100 text-lg max-w-3xl">
            Browse tournament fields, seeds, and full results with match-by-match scoring
            breakdowns. See how every wrestler performed and how fantasy points were earned.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-12">
        {loading && (
          <div className="min-h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
          </div>
        )}

        {error && (
          <div className="text-center py-16">
            <p className="text-gray-600">Unable to load tournaments. Please try again later.</p>
          </div>
        )}

        {!loading && !error && tournaments.length === 0 && (
          <div className="text-center py-16">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No tournaments yet</h2>
            <p className="text-gray-600">
              Tournaments will appear here as they're added. Check back soon!
            </p>
          </div>
        )}

        {/* Live Tournaments */}
        {live.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Live Now</h2>
            <p className="text-gray-600 mb-6">
              These tournaments are currently in progress. Results and scores update as matches are
              completed.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {live.map((t) => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>
          </section>
        )}

        {/* Upcoming Tournaments */}
        {upcoming.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Upcoming Tournaments</h2>
            <p className="text-gray-600 mb-6">
              Preview the field and seeds for upcoming tournaments. Create a league and draft your
              roster before the action starts.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcoming.map((t) => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>
          </section>
        )}

        {/* Ad banner between sections */}
        <AdBanner slot={AD_SLOTS.tournaments} format="horizontal" />

        {/* Completed Tournaments */}
        {completed.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Past Tournaments</h2>
            <p className="text-gray-600 mb-6">
              Full results from completed tournaments including original seeds, final placements,
              and detailed match-by-match fantasy scoring breakdowns for every wrestler.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {completed.map((t) => (
                <TournamentCard key={t.id} tournament={t} />
              ))}
            </div>
          </section>
        )}

        {/* SEO-friendly contextual text */}
        <section className="bg-gray-50 rounded-xl p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            About Tournament Results on Takedown League
          </h2>
          <div className="text-gray-600 space-y-3 text-sm leading-relaxed">
            <p>
              Every tournament on Takedown League includes the complete field with original seeds,
              final placements, and a full scoring breakdown. Click into any tournament to see
              results by weight class, then expand individual wrestlers to view their match-by-match
              results including win type, opponent, and fantasy points earned.
            </p>
            <p>
              Fantasy scoring is based on real NCAA wrestling tournament results. Wrestlers earn
              placing points based on where they finish (1st through 8th) and match points for each
              win during the tournament. Bonus points are awarded for major decisions, tech falls,
              falls, and forfeits. This unique scoring system rewards both tournament placement and
              dominance throughout the bracket.
            </p>
            <p>
              Want to compete?{' '}
              <Link
                to="/login?tab=register"
                className="text-purple-600 hover:text-purple-800 font-medium"
              >
                Create a free account
              </Link>
              , start a league with friends, and draft wrestlers for any upcoming tournament. Your
              team earns points as real results come in.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 rounded-xl py-10 px-6 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">Ready to compete?</h2>
          <p className="text-purple-100 mb-6">
            Create a free Takedown League and draft wrestlers with friends. Earn points based on
            real tournament results.
          </p>
          <Link
            to="/login?tab=register"
            className="inline-block bg-white text-purple-700 font-semibold py-3 px-8 rounded-lg hover:bg-purple-50 transition-colors text-lg"
          >
            Create a Free Account
          </Link>
        </section>
      </div>
    </PublicLayout>
  );
}
